<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { usePeerLanguageOptions } from '../usePeerLanguageOptions';
import logger from '../logger';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  draftText: { type: String, default: '' },
});
const emit = defineEmits(['apply-translation']);

const { t } = useI18n();
const store = useStore();
const { labelFor: peerLangLabelFor } = usePeerLanguageOptions();

const prefs = computed(() => store.getters.prefsFor(props.conversationId));
const preview = computed(() =>
  store.getters.outboundPreviewFor(props.conversationId)
);

const peerLangLabel = computed(() =>
  peerLangLabelFor(prefs.value.peerLang)
);

const isLoading = ref(false);
const errorMsg = ref('');

const runPreview = async () => {
  if (!props.draftText?.trim()) return;
  errorMsg.value = '';
  isLoading.value = true;
  try {
    await store.dispatch('previewOutbound', {
      conversationId: props.conversationId,
      text: props.draftText,
      peerLang: prefs.value.peerLang,
    });
    logger.info('outbound.preview.ok');
  } catch (err) {
    errorMsg.value = err?.response?.data?.error || t('CUSTOM.TRANSLATION.FAILED');
  } finally {
    isLoading.value = false;
  }
};

const clearPreview = () => {
  store.dispatch('clearOutboundPreview', props.conversationId);
};

watch(
  () => props.draftText,
  () => {
    if (preview.value && preview.value.source !== props.draftText) {
      clearPreview();
    }
  }
);

const applyTranslation = () => {
  if (!preview.value?.translated) return;
  emit('apply-translation', preview.value.translated);
  clearPreview();
};

defineExpose({ runPreview, clearPreview });
</script>

<template>
  <div
    v-if="prefs.outboundPreviewOn"
    class="px-3 py-2 border-t border-n-weak bg-n-alpha-1"
  >
    <div class="flex items-center justify-between mb-1">
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="px-2 py-0.5 text-xs rounded border border-n-weak hover:bg-n-alpha-2"
          :disabled="isLoading || !draftText"
          @click="runPreview"
        >
          {{
            isLoading
              ? t('CUSTOM.TRANSLATION.TRANSLATING')
              : preview
                ? t('CUSTOM.TRANSLATION.RETRANSLATE')
                : t('CUSTOM.TRANSLATION.TRANSLATE')
          }}
        </button>
        <button
          v-if="preview"
          type="button"
          class="px-2 py-0.5 text-xs rounded text-n-brand hover:bg-n-alpha-2"
          @click="applyTranslation"
        >
          {{ t('CUSTOM.TRANSLATION.APPLY_TO_REPLY') }}
        </button>
      </div>
      <div class="text-[10px] uppercase tracking-wide text-n-slate-10">
        {{ t('CUSTOM.TRANSLATION.PREVIEW_META', { lang: peerLangLabel }) }}
      </div>
    </div>
    <div v-if="preview?.translated" class="text-sm whitespace-pre-wrap text-n-slate-12">
      {{ preview.translated }}
    </div>
    <div v-else-if="errorMsg" class="text-xs text-n-ruby-9">
      {{ errorMsg }}
    </div>
    <div v-else class="text-xs italic text-n-slate-10">
      {{ t('CUSTOM.TRANSLATION.PREVIEW_HINT', { lang: peerLangLabel }) }}
    </div>
  </div>
</template>
